import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import filePdfO from '@iconify/icons-fa/file-pdf-o'
import fileTextO from '@iconify/icons-fa/file-text-o'
import fileExcelO from '@iconify/icons-fa/file-excel-o'
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  XMarkIcon,
  TrashIcon,
  EyeIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import {
  MainPage,
  PageTitle,
  SecondaryButton,
  ModalQuestion,
  Modal,
  FormText,
  FormSelect,
  PaginationFooter,
} from '../../components'
import Dropdown from '../../components/Atoms/Dropdown'
import { ImportProgressBar } from '../../components/ShipmentBulkUpload/ImportProgressBar'
import { useBulkUploadProgress } from '../../hooks/useBulkUploadProgress'
import { useAppSelector } from '../../store/hooks'
import {
  getImport,
  getImportFile,
  cancelImport,
  deleteImport,
  executeImport,
  deleteImportDetail,
} from '../../services/ShipmentBulkUploadServices'
import { alertService } from '../../services'
import {
  exportTablePDF,
  exportTableXLSX,
  exportTableTXT,
} from '../../utils/exports'
import {
  headerStatusMeta,
  detailStatusMeta,
  resolveDetailStatusName,
  messageForCode,
  getBulkActionFlags,
  SERVER_ERROR_RETRY_CAP,
} from '../../utils/shipmentBulkUploadStatus'
import {
  ImportDetailDTO,
  ImportHeaderDTO,
  ImportHeaderStatusName,
  ImportDetailStatusName,
  ImportProgressDTO,
} from '../../interfaces/Import/ShipmentBulkUploadApi'

const LIST_ROUTE = '/shipment/bulk-upload/list'

function fmt(dt?: string | null): string {
  if (!dt) return '–'
  const d = new Date(dt)
  if (isNaN(d.getTime())) return dt
  return d.toLocaleString('es-VE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}

/** Etiqueta del dueño de la carga: nombre resuelto (sitio/cuenta) o, si falta, el ID. */
function ownerLabel(h: ImportHeaderDTO): string {
  if (h.billing_account_name) return h.billing_account_name
  if (h.account_name) return h.account_name
  if (h.billing_account != null && h.billing_account !== 0)
    return `Sitio ${h.billing_account}`
  if (h.account_id) return h.account_id
  return '–'
}

const ShipmentBulkUploadDetail: FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const headerId = id ?? null

  const user = useAppSelector((s) => s.user)

  const [header, setHeader] = useState<ImportHeaderDTO | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(true)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [actionBusy, setActionBusy] = useState(false)
  const [restartSignal, setRestartSignal] = useState(0)

  // Tabla de detalles: buscador (cualquier columna), paginado y page-size (client-side,
  // los detalles llegan todos en el GET). Mismo patrón que FavoriteDestination/consult.
  const [detailSearch, setDetailSearch] = useState('')
  const [detailPage, setDetailPage] = useState(0)
  const [detailPageSize, setDetailPageSize] = useState(10)

  // Modal de confirmación (cancelar / eliminar / eliminar detalle)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmTitle, setConfirmTitle] = useState('')
  const pendingAction = useRef<(() => void) | null>(null)

  // Modal ver payload (read-only)
  const [payloadOpen, setPayloadOpen] = useState(false)
  const [payloadText, setPayloadText] = useState('')

  // Info del header: al hacer click se despliega hacia abajo (hover = tooltip).
  const [infoOpen, setInfoOpen] = useState(false)

  const prevDone = useRef(0)

  const fetchDetail = useCallback(async () => {
    if (!headerId) return
    const res = await getImport(headerId)
    if (res.didError) {
      setDetailError(messageForCode(res.code, res.errorMessage))
      setLoadingDetail(false)
      return
    }
    setHeader(res.model)
    setDetailError(null)
    setLoadingDetail(false)
  }, [headerId])

  // Carga inicial + recarga tras acciones (restartSignal).
  useEffect(() => {
    setLoadingDetail(true)
    fetchDetail()
  }, [fetchDetail, restartSignal])

  // Al completar: recargar el detalle final. El reintento de errores de servidor (78)
  // es manual (botón Reintentar) o del propio backend; no hay auto-retry del cliente.
  const onComplete = useCallback(
    async (_p: ImportProgressDTO) => {
      if (!headerId) return
      const res = await getImport(headerId)
      if (res.didError || !res.model) return
      setHeader(res.model)
    },
    [headerId]
  )

  const { progress, connectionLost, notFound } = useBulkUploadProgress(
    headerId,
    {
      onComplete,
      restartSignal,
    }
  )

  // Recargar detalle cuando avanza el conteo (filas nuevas procesadas).
  useEffect(() => {
    if (!progress) return
    const done = (progress.executed_count ?? 0) + (progress.error_count ?? 0)
    if (done !== prevDone.current) {
      prevDone.current = done
      if (!progress.is_complete) fetchDetail()
    }
  }, [progress, fetchDetail])

  // --- Estado derivado ---
  const statusName = progress?.status_name ?? header?.status_name ?? null
  const statusId = progress?.status_id ?? header?.status_id ?? null
  const meta = headerStatusMeta(statusName, statusId)
  const isInProcess = statusName === ImportHeaderStatusName.InProcess
  const isFinalizedWithErrors =
    statusName === ImportHeaderStatusName.FinalizedWithErrors
  const isFinalized = statusName === ImportHeaderStatusName.Finalized

  // Detalle completo del header, sin truncar. Se usa tanto en el tooltip (hover)
  // como en el panel desplegable (click). Devuelve null si aún no hay header.
  const renderHeaderInfo = () => {
    if (!header) return null
    return (
      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5 text-sm">
        <dt className="text-gray-400">Estatus</dt>
        <dd className="text-gray-800">{meta.label}</dd>
        <dt className="text-gray-400">Fecha Inicio</dt>
        <dd className="text-gray-800">{fmt(header.start_date)}</dd>
        {header.end_date && (
          <>
            <dt className="text-gray-400">Fecha Fin</dt>
            <dd className="text-gray-800">{fmt(header.end_date)}</dd>
          </>
        )}
        <dt className="text-gray-400">Por</dt>
        <dd className="break-words text-gray-800">
          {header.creation_user_name ?? '–'}
        </dd>
        <dt className="text-gray-400">Línea de facturación</dt>
        <dd className="break-words text-gray-800">{ownerLabel(header)}</dd>
      </dl>
    )
  }

  const details = useMemo(() => header?.details ?? [], [header])
  // Estado de los botones derivado de agregar los estatus de los detalles + la cabecera
  // (la spec exige calcularlo desde los detalles, no desde progress.*_count).
  const flags = useMemo(
    () => getBulkActionFlags(details, statusName, statusId),
    [details, statusName, statusId]
  )
  const executedCount = flags.executedCount
  const errorCount = flags.dataErrorCount + flags.serverErrorCount

  // Buscador por cualquier columna (línea, estado, guía, guía aliada, mensaje, fecha).
  const filteredDetails = useMemo(() => {
    const tokens = detailSearch.toLowerCase().split(' ').filter(Boolean)
    if (!tokens.length) return details
    return details.filter((d) => {
      const haystack = [
        d.line_number,
        detailStatusMeta(d.status_name, d.status_id).label,
        d.shipment_number,
        d.shipment_header_reference,
        d.error_message,
        fmt(d.executed_date),
      ]
        .map((v) => (v ?? '').toString().toLowerCase())
        .join(' ')
      return tokens.every((t) => haystack.includes(t))
    })
  }, [details, detailSearch])

  const paginatedDetails = useMemo(() => {
    const start = detailPage * detailPageSize
    return filteredDetails.slice(start, start + detailPageSize)
  }, [filteredDetails, detailPage, detailPageSize])

  // Export (PDF/Excel/TXT) del conjunto FILTRADO completo (no solo la página visible).
  const DETAIL_EXPORT_COLUMNS = [
    'Línea',
    'Estado',
    'Guía',
    'Guía aliada',
    'Mensaje',
    'Procesado',
  ]
  const buildDetailExportRows = (): (string | number)[][] =>
    filteredDetails.map((d) => [
      d.line_number,
      detailStatusMeta(d.status_name, d.status_id).label,
      d.shipment_number ?? '',
      d.shipment_header_reference ?? '',
      d.error_message ?? '',
      fmt(d.executed_date),
    ])

  // --- Acciones ---
  const openConfirm = (title: string, action: () => void) => {
    setConfirmTitle(title)
    pendingAction.current = action
    setConfirmOpen(true)
  }
  const onAccepts: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
    if (value === true && pendingAction.current) pendingAction.current()
    pendingAction.current = null
  }

  const runAction = async (
    fn: () => Promise<{
      didError: boolean
      code: string
      errorMessage: string
    }>,
    okMsg: string,
    after?: () => void
  ) => {
    setActionBusy(true)
    const r = await fn()
    setActionBusy(false)
    if (r.didError) {
      alertService.error(
        'No se pudo completar',
        messageForCode(r.code, r.errorMessage)
      )
      return
    }
    alertService.success(okMsg)
    if (after) after()
    else setRestartSignal((s) => s + 1)
  }

  const doCancel = () =>
    runAction(() => cancelImport(headerId!), 'Carga masiva cancelada')

  const doDelete = () =>
    runAction(
      () => deleteImport(headerId!),
      'Carga masiva eliminada',
      () => navigate(LIST_ROUTE)
    )

  const doRetry = () =>
    runAction(
      () => executeImport(headerId!, true),
      'Reintentando errores',
      () => setRestartSignal((s) => s + 1)
    )

  const doDeleteDetail = (detailId: string) =>
    runAction(
      () => deleteImportDetail(headerId!, detailId),
      'Detalle eliminado'
    )

  const doDownload = async () => {
    if (!headerId) return
    setActionBusy(true)
    const r = await getImportFile(headerId)
    setActionBusy(false)
    if (r.didError || !r.model?.url) {
      alertService.error(
        'No se pudo descargar',
        messageForCode(r.code, r.errorMessage)
      )
      return
    }
    window.open(r.model.url, '_blank', 'noopener')
  }

  const openPayload = (d: ImportDetailDTO) => {
    setPayloadText(prettyJson(d.payload_json))
    setPayloadOpen(true)
  }

  // --- Render ---
  const importationNumber = header?.bulk_upload_number ?? 'Carga masiva'

  if (notFound) {
    return (
      <MainPage>
        <PageTitle
          title="Carga masiva"
          back
          onBack={() => navigate(LIST_ROUTE)}
        />
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          La carga masiva no existe o fue eliminada.
        </div>
      </MainPage>
    )
  }

  return (
    <MainPage>
      <PageTitle
        title={importationNumber}
        back
        onBack={() => navigate(LIST_ROUTE)}
      />

      {detailError && !header && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {detailError}
        </div>
      )}

      {loadingDetail && !header ? (
        <div className="animate-pulse space-y-4">
          <div className="h-24 rounded-lg bg-gray-100" />
          <div className="h-12 rounded-lg bg-gray-100" />
          <div className="h-64 rounded-lg bg-gray-100" />
        </div>
      ) : (
        header && (
          <div className="space-y-5">
            {/* Header card */}
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                {/* Grupo de info: hover = tooltip flotante, click = panel desplegable */}
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={infoOpen}
                  onClick={() => setInfoOpen((v) => !v)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setInfoOpen((v) => !v)
                    }
                  }}
                  className="group relative flex min-w-0 cursor-pointer items-center gap-3 rounded-md py-0.5 pr-6 hover:bg-gray-50/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-main-400"
                >
                  <span
                    className={`shrink-0 whitespace-nowrap rounded-full px-3 py-0.5 text-xs font-semibold ${meta.badgeClass}`}
                  >
                    {meta.label}
                  </span>
                  <span className="max-w-[50rem] truncate whitespace-nowrap text-sm text-gray-500">
                    Fecha Inicio {fmt(header.start_date)}
                    {header.end_date
                      ? ` · Fecha Fin ${fmt(header.end_date)}`
                      : ''}
                  </span>
                  <span className="max-w-[12rem] truncate text-sm text-gray-500">
                    Por {header.creation_user_name}
                  </span>
                  <span className="max-w-[45rem] truncate text-sm text-gray-500">
                    Línea de facturación {ownerLabel(header)}
                  </span>
                  <ChevronDownIcon
                    className={`absolute right-1 h-4 w-4 shrink-0 text-gray-400 transition-transform ${
                      infoOpen ? 'rotate-180' : ''
                    }`}
                  />
                  {/* Tooltip en hover (oculto cuando el panel ya está desplegado) */}
                  {!infoOpen && (
                    <div className="pointer-events-none absolute left-0 top-full z-20 mt-2 hidden max-h-64 w-max max-w-[36rem] overflow-auto rounded-lg border border-gray-200 bg-white p-3 shadow-lg group-hover:block">
                      {renderHeaderInfo()}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {errorCount > 0 && (
                    <SecondaryButton
                      onClick={doRetry}
                      disabled={!flags.canRetry || actionBusy}
                      className="h-9 text-xs"
                    >
                      <ArrowPathIcon className="mr-1 h-4 w-4" /> Reintentar
                    </SecondaryButton>
                  )}
                  {errorCount > 0 && (
                    <SecondaryButton
                      onClick={() =>
                        navigate(`/shipment/bulk-upload/${headerId}/fix`)
                      }
                      disabled={!flags.canEditErrors || actionBusy}
                      className="h-9 text-xs"
                    >
                      <PencilSquareIcon className="mr-1 h-4 w-4" /> Corregir
                      errores
                    </SecondaryButton>
                  )}
                  {isInProcess && (
                    <SecondaryButton
                      onClick={() =>
                        openConfirm(
                          'Vas a cancelar la carga masiva. Las guías ya creadas quedan en el sistema; las pendientes no se procesarán. ¿Continuar?',
                          doCancel
                        )
                      }
                      disabled={actionBusy}
                      className="h-9 text-xs"
                    >
                      <XMarkIcon className="mr-1 h-4 w-4" /> Cancelar
                    </SecondaryButton>
                  )}
                  {(isInProcess || isFinalizedWithErrors) && (
                    <SecondaryButton
                      onClick={() =>
                        openConfirm(
                          'Esta carga masiva se eliminará y no podrá recuperarse. ¿Continuar?',
                          doDelete
                        )
                      }
                      disabled={!flags.canDeleteHeader || actionBusy}
                      title={
                        !flags.canDeleteHeader
                          ? 'No se puede eliminar: ya hay guías generadas en esta carga'
                          : undefined
                      }
                      className="h-9 text-xs"
                    >
                      <TrashIcon className="mr-1 h-4 w-4" /> Eliminar
                    </SecondaryButton>
                  )}
                  {header.source_file_path && (
                    <SecondaryButton
                      onClick={doDownload}
                      disabled={actionBusy}
                      className="h-9  text-xs"
                      title={
                        header.source_file_name
                          ? `Descargar ${header.source_file_name}`
                          : 'Descargar archivo'
                      }
                    >
                      <ArrowDownTrayIcon className="mr-1 h-4 w-4" /> Descargar
                      archivo
                    </SecondaryButton>
                  )}
                </div>
              </div>

              {/* Panel desplegable (click): crece hacia abajo sin alterar la fila,
                  con tope de altura y scroll para no romper la vista. */}
              {infoOpen && (
                <div className="mt-3 max-h-64 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3">
                  {renderHeaderInfo()}
                </div>
              )}
            </div>

            {/* Banner reconectando (CRITICAL GAP: nunca congela en silencio) */}
            {connectionLost && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800"
                aria-live="polite"
              >
                Reconectando… mostrando el último progreso conocido.
              </div>
            )}

            {/* Progreso */}
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <ImportProgressBar progress={progress} />
              {isFinalized && (
                <p className="mt-3 rounded-md bg-cream-main/40 px-3 py-2 text-sm font-medium text-gray-800">
                  ✓ Carga masiva completada con éxito
                </p>
              )}
              {isFinalizedWithErrors && (
                <p className="mt-3 text-sm font-medium text-amber-700">
                  ⚠ {executedCount} de {header.total_details} guías creadas.{' '}
                  {errorCount} con error.
                </p>
              )}
            </div>

            {/* Toolbar: buscador + page-size + export */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="w-full sm:max-w-xs">
                <FormText
                  id="detail-search"
                  name="detail-search"
                  value={detailSearch}
                  placeholder="Buscar en cualquier columna…"
                  containerClassname="flex flex-1"
                  onChange={(e: any) => {
                    setDetailSearch(e.target.value)
                    setDetailPage(0)
                  }}
                />
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center text-xs text-gray-600">
                  <span>Mostrar</span>
                  <div className="mx-1 inline">
                    <FormSelect
                      id="detail-page-size"
                      name="detail-page-size"
                      label=""
                      selected={detailPageSize}
                      options={[10, 25, 50, 100]}
                      className="!h-8 !w-20 !px-2"
                      optionString={(o) => o.toString()}
                      onSelectOption={(o) => {
                        setDetailPageSize(o)
                        setDetailPage(0)
                      }}
                    />
                  </div>
                  <span>registros</span>
                </div>
                <div className="flex items-center gap-2">
                  <Dropdown
                    align="left"
                    label="Exportar"
                    items={[
                      {
                        items: [
                          {
                            label: 'EXCEL',
                            icon: (props) => (
                              <Icon
                                icon={fileExcelO}
                                className={props.className}
                              />
                            ),
                            onClick: () => {
                              if (!filteredDetails.length) return
                              exportTableXLSX(
                                DETAIL_EXPORT_COLUMNS,
                                buildDetailExportRows(),
                                'Detalle',
                                `CargaMasiva_${header.bulk_upload_number}`
                              )
                            },
                          },
                          {
                            label: 'PDF',
                            icon: (props) => (
                              <Icon
                                icon={filePdfO}
                                className={props.className}
                              />
                            ),
                            onClick: () => {
                              if (!filteredDetails.length) return
                              exportTablePDF(
                                user.user!,
                                `CARGA MASIVA ${header.bulk_upload_number}`,
                                DETAIL_EXPORT_COLUMNS,
                                buildDetailExportRows(),
                                `CargaMasiva_${header.bulk_upload_number}`
                              )
                            },
                          },
                          {
                            label: 'TXT',
                            icon: (props) => (
                              <Icon
                                icon={fileTextO}
                                className={props.className}
                              />
                            ),
                            onClick: () => {
                              if (!filteredDetails.length) return
                              exportTableTXT(
                                DETAIL_EXPORT_COLUMNS,
                                buildDetailExportRows(),
                                `CargaMasiva_${header.bulk_upload_number}`
                              )
                            },
                          },
                        ],
                      },
                    ]}
                    buttonClassName="bg-main-500 hover:bg-main-400 hover:border-main-400 text-white focus-visible:outline-main-400 !pt-1 !pb-1 !text-xs !ring-0 h-[32px] items-center"
                    menuClassName="!left-0 !top-full !mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Tabla de detalles */}
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Nº</th>
                    <th className="px-3 py-2 font-medium">Estatus</th>
                    <th className="px-3 py-2 font-medium">Nº de guía</th>
                    <th className="px-3 py-2 font-medium">Nº de guía aliada</th>
                    <th className="px-3 py-2 font-medium">Mensaje</th>
                    <th className="px-3 py-2 font-medium">
                      Fecha de procesado
                    </th>
                    <th className="px-3 py-2 font-medium text-right">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedDetails.length === 0 && (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-3 py-8 text-center text-sm text-gray-500"
                      >
                        {detailSearch
                          ? 'No hay detalles que coincidan con la búsqueda.'
                          : 'No hay detalles.'}
                      </td>
                    </tr>
                  )}
                  {paginatedDetails.map((d) => {
                    const dm = detailStatusMeta(d.status_name, d.status_id)
                    const canonical = resolveDetailStatusName(
                      d.status_name,
                      d.status_id
                    )
                    const isExec = canonical === ImportDetailStatusName.Executed
                    const isErrServer =
                      canonical === ImportDetailStatusName.ErrorServer
                    // Filas con payload que vale la pena inspeccionar (errores + sustituida).
                    const isErrorish =
                      canonical === ImportDetailStatusName.Error ||
                      canonical === ImportDetailStatusName.ErrorProcessing ||
                      canonical === ImportDetailStatusName.ErrorServer ||
                      canonical === ImportDetailStatusName.Substituted
                    // Se puede eliminar salvo cuando ya generó el envío real (74).
                    const canDelete =
                      canonical !== ImportDetailStatusName.Executed
                    return (
                      <tr key={d.shipment_bulk_upload_detail_id}>
                        <td className="px-3 py-2 tabular-nums text-gray-700">
                          {d.line_number}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${dm.badgeClass}`}
                          >
                            {dm.label}
                          </span>
                          {isErrServer && (
                            <div className="mt-0.5 text-[11px] text-gray-400">
                              intento {d.retry_count ?? 0}/
                              {SERVER_ERROR_RETRY_CAP}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-700">
                          {d.shipment_number ?? '–'}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-700">
                          {d.shipment_header_reference ?? '–'}
                        </td>
                        <td
                          className="px-3 py-2 text-red-600 max-w-xs break-words whitespace-normal align-top"
                          title={d.error_message ?? undefined}
                        >
                          {d.error_message ?? '–'}
                        </td>
                        <td className="px-3 py-2 tabular-nums text-gray-500">
                          {fmt(d.executed_date)}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex justify-end gap-2">
                            {isExec && (
                              <button
                                title="Ver guía"
                                onClick={() =>
                                  navigate(
                                    d.shipment_number
                                      ? `/shipments/consult?query=${encodeURIComponent(
                                          d.shipment_number
                                        )}`
                                      : '/shipments/consult'
                                  )
                                }
                                className="text-main-500 hover:text-main-400"
                              >
                                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                              </button>
                            )}
                            {isErrorish && (
                              <button
                                title="Ver payload"
                                onClick={() => openPayload(d)}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                title="Eliminar este detalle"
                                onClick={() =>
                                  openConfirm(
                                    `¿Eliminar la guía de la línea ${d.line_number}? No se procesará.`,
                                    () =>
                                      doDeleteDetail(
                                        d.shipment_bulk_upload_detail_id
                                      )
                                  )
                                }
                                disabled={actionBusy}
                                className="text-gray-500 hover:text-red-600"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <PaginationFooter
              currentPage={detailPage}
              itemsPerPage={detailPageSize}
              totalItems={filteredDetails.length}
              onPageChange={setDetailPage}
            />

            {/* Logs (últimos 200, solo WARNING/ERROR) */}
            {header.logs && header.logs.length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h2 className="mb-2 text-sm font-semibold text-gray-700">
                  Logs
                </h2>
                <ul className="space-y-1 text-xs">
                  {header.logs.map((l) => (
                    <li
                      key={l.shipment_bulk_upload_log_id}
                      className="flex gap-2 text-gray-600"
                    >
                      <span
                        className={
                          l.event_type === 'ERROR'
                            ? 'font-semibold text-red-600'
                            : 'font-semibold text-amber-600'
                        }
                      >
                        {l.event_type}
                      </span>
                      <span className="tabular-nums text-gray-400">
                        {fmt(l.log_date)}
                      </span>
                      <span>{l.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )
      )}

      <ModalQuestion
        open={confirmOpen}
        title={confirmTitle}
        setOpen={setConfirmOpen}
        setAccepts={onAccepts}
      />

      <Modal
        open={payloadOpen}
        setOpen={setPayloadOpen}
        style={{ maxWidth: '40rem' }}
      >
        <div className="flex items-center justify-between border-b border-gray-200 pb-2">
          <h2 className="text-base font-semibold text-gray-800">
            Guía (payload)
          </h2>
          <button onClick={() => setPayloadOpen(false)}>
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <pre className="mt-3 max-h-96 overflow-auto rounded bg-gray-50 p-3 text-xs text-gray-700">
          {payloadText}
        </pre>
      </Modal>
    </MainPage>
  )
}

export default ShipmentBulkUploadDetail
